import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { Add, Edit, Delete, Save, Cancel, Upload } from '@mui/icons-material';
import AppService from '../../../services/AppService';
import { showNotification } from '../../../utils/notifications';
import { showSuccessMessage, showErroMessage } from '../../../LayoutContainers/AppLayout/redux/AppSlice';

// Helper to decode JWT and extract roles
const getTokenInfo = () => {
  try {
    const authInfo = localStorage.getItem('authInfo');
    const token = authInfo ? JSON.parse(authInfo)?.token : null;
    if (!token) return null;
    const [, payloadB64] = token.split('.') || [];
    if (!payloadB64) return null;
    const payload = JSON.parse(atob(payloadB64));
    return {
      roles: (payload?.roles || []).map(r => r.authority || r).concat(payload?.authorities || [])
    };
  } catch (e) {
    return null;
  }
};

const PositionsSettings = ({ organizationId }) => {
  const dispatch = useDispatch();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [newPositionName, setNewPositionName] = useState('');
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState(null);
  
  // Import positions states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importablePositions, setImportablePositions] = useState([]);
  const [selectedImportPositions, setSelectedImportPositions] = useState([]);
  const [loadingImportable, setLoadingImportable] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Check if user is ROOT admin
  const tokenInfo = getTokenInfo();
  const isRootAdmin = tokenInfo?.roles?.includes('ROLE_ROOT_ADMIN') || false;

  // Fetch positions from organization config
  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    fetchPositions();
  }, [organizationId]);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const response = await AppService.get(`/v1/organization-config/organization/${organizationId}`);
      setPositions(response?.positions || []);
      setError('');
    } catch (err) {
      console.error('Error fetching positions:', err);
      setError('Failed to load positions');
      setPositions([]);
    } finally {
      setLoading(false);
    }
  };

  // Add new position
  const handleAddPosition = () => {
    if (!newPositionName.trim()) {
      showNotification('Position name cannot be empty', 'error');
      return;
    }

    // Check for duplicates
    if (positions.some(p => p.positionName.toLowerCase() === newPositionName.trim().toLowerCase())) {
      showNotification('Position already exists', 'error');
      return;
    }

    const newPosition = {
      id: `temp-${Date.now()}`, // Temporary ID for frontend
      positionName: newPositionName.trim(),
    };

    setPositions([...positions, newPosition]);
    setNewPositionName('');
    showNotification('Position added. Click Save to apply changes.', 'info');
  };

  // Start editing a position
  const handleEditClick = (position) => {
    setEditingId(position.id);
    setEditingName(position.positionName);
  };

  // Save edited position
  const handleEditSave = (id) => {
    if (!editingName.trim()) {
      showNotification('Position name cannot be empty', 'error');
      return;
    }

    // Check for duplicates (excluding current)
    if (positions.some(p => p.id !== id && p.positionName.toLowerCase() === editingName.trim().toLowerCase())) {
      showNotification('Position already exists', 'error');
      return;
    }

    setPositions(positions.map(p =>
      p.id === id ? { ...p, positionName: editingName.trim() } : p
    ));
    setEditingId(null);
    setEditingName('');
    showNotification('Position updated. Click Save to apply changes.', 'info');
  };

  // Cancel editing
  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  // Delete position
  const handleDeleteClick = (position) => {
    setPositionToDelete(position);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setPositions(positions.filter(p => p.id !== positionToDelete.id));
    setDeleteDialogOpen(false);
    setPositionToDelete(null);
    showNotification('Position removed. Click Save to apply changes.', 'info');
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setPositionToDelete(null);
  };

  // Fetch importable positions
  const fetchImportablePositions = async () => {
    try {
      setLoadingImportable(true);
      const response = await AppService.get('/v1/positions/importable');
      setImportablePositions(response?.positions || []);
      setSelectedImportPositions([]);
      setError('');
    } catch (err) {
      console.error('Error fetching importable positions:', err);
      showNotification('Failed to load importable positions', 'error');
      setImportablePositions([]);
    } finally {
      setLoadingImportable(false);
    }
  };

  // Open import dialog
  const handleOpenImportDialog = async () => {
    setImportDialogOpen(true);
    await fetchImportablePositions();
  };

  // Close import dialog
  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportablePositions([]);
    setSelectedImportPositions([]);
  };

  // Toggle position selection
  const handleTogglePosition = (positionName) => {
    setSelectedImportPositions(prev => {
      if (prev.includes(positionName)) {
        return prev.filter(p => p !== positionName);
      } else {
        return [...prev, positionName];
      }
    });
  };

  // Toggle select all positions
  const handleToggleSelectAll = () => {
    if (selectedImportPositions.length === importablePositions.length) {
      setSelectedImportPositions([]);
    } else {
      setSelectedImportPositions([...importablePositions]);
    }
  };

  // Import selected positions
  const handleImportPositions = async () => {
    if (selectedImportPositions.length === 0) {
      showNotification('Please select at least one position to import', 'warning');
      return;
    }

    try {
      setImporting(true);
      const response = await AppService.post(
        `/v1/organizations/${organizationId}/positions/import`,
        { positions: selectedImportPositions }
      );

      // Update positions list with response data
      if (response?.positions) {
        setPositions(response.positions);
        dispatch(showSuccessMessage(
          `Successfully imported ${selectedImportPositions.length} position(s)`
        ));
        handleCloseImportDialog();
      } else {
        dispatch(showErroMessage('Positions imported but no data returned'));
        handleCloseImportDialog();
      }
    } catch (err) {
      console.error('Error importing positions:', err);
      // Handle different error response structures
      const errorMessage = err?.response?.data?.message || 
                          err?.message || 
                          err?.data?.message ||
                          'Failed to import positions';
      dispatch(showErroMessage(errorMessage));
    } finally {
      setImporting(false);
    }
  };

  // Save all changes to backend
  const handleSaveAll = async () => {
    if (!organizationId) {
      showNotification('Organization ID is missing', 'error');
      return;
    }

    setSaving(true);
    try {
      // Remove temporary IDs and send to backend in required format
      const positionsToSave = positions.map(({ id, positionName }) => ({
        ...(id && !id.toString().startsWith('temp-') ? { id } : {}),
        positionName
      }));

      // Send with positions key as required
      await AppService.put(
        `/v1/organization-config/positions/organization/${organizationId}`,
        { positions: positionsToSave }
      );

      showNotification('Positions saved successfully', 'success');
      // Refresh to get server-assigned IDs
      await fetchPositions();
    } catch (err) {
      console.error('Error saving positions:', err);
      showNotification(err?.response?.data?.message || 'Failed to save positions', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!organizationId) {
    return (
      <Alert severity="warning">
        Please save the organization configuration first before managing positions.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Manage Positions
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Add, edit, or remove positions for your organization. Click Save to apply changes.
          </Typography>
        </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Upload />}
            onClick={handleOpenImportDialog}
            sx={{ px: 3, py: 1 }}
          >
            Import
          </Button>
      </Box>

      {/* Add new position */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'background.default', borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <TextField
              fullWidth
              label="New Position Name"
              value={newPositionName}
              onChange={(e) => setNewPositionName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddPosition();
                }
              }}
              placeholder="e.g., Manager, Developer, Analyst"
              variant="outlined"
              size="medium"
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddPosition}
              disabled={!newPositionName.trim()}
              sx={{ height: 56, px: 3 }}
            >
              Add Position
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* List of positions */}
      {positions.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No positions added yet. Add your first position above.
        </Alert>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2, border: '1px solid #ddd', overflow: 'hidden', mt: 2, backgroundColor: '#fff',}}>
          <List sx={{p: 1 }}>
            {positions.map((position, index) => (
              <ListItem
                key={position.id}
                divider={index < positions.length - 1}
                sx={{
                  px: 2,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {editingId === position.id ? (
                  <>
                    <TextField
                      fullWidth
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleEditSave(position.id);
                        }
                      }}
                      autoFocus
                      size="small"
                    />
                    <ListItemSecondaryAction sx={{ right: 16 }}>
                      <IconButton
                        edge="end"
                        onClick={() => handleEditSave(position.id)}
                        color="primary"
                        sx={{ mr: 1 }}
                      >
                        <Save />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={handleEditCancel}
                      >
                        <Cancel />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </>
                ) : (
                  <>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {position.positionName}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction sx={{ right: 16 }}>
                      <IconButton
                        edge="end"
                        onClick={() => handleEditClick(position)}
                        color="primary"
                        sx={{ mr: 1 }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteClick(position)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </>
                )}
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Save button */}
      <Box mt={4} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<Save />}
          onClick={handleSaveAll}
          disabled={saving || positions.length === 0}
          size="large"
          sx={{ px: 4, py: 1.5, fontWeight: 600 }}
        >
          {saving ? 'Saving...' : 'Save Positions'}
        </Button>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the position "{positionToDelete?.positionName}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import positions dialog */}
      <Dialog 
        open={importDialogOpen} 
        onClose={handleCloseImportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Upload sx={{ mr: 1 }} />
            Import Positions
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingImportable ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : importablePositions.length === 0 ? (
            <Alert severity="info">
              No positions available to import.
            </Alert>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Select the positions you want to import:
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedImportPositions.length === importablePositions.length && importablePositions.length > 0}
                      indeterminate={selectedImportPositions.length > 0 && selectedImportPositions.length < importablePositions.length}
                      onChange={handleToggleSelectAll}
                    />
                  }
                  label="Select All"
                />
              </Box>
              <FormGroup>
                {importablePositions.map((positionName) => (
                  <FormControlLabel
                    key={positionName}
                    control={
                      <Checkbox
                        checked={selectedImportPositions.includes(positionName)}
                        onChange={() => handleTogglePosition(positionName)}
                      />
                    }
                    label={positionName}
                  />
                ))}
              </FormGroup>
              {selectedImportPositions.length > 0 && (
                <Typography variant="caption" color="primary" sx={{ mt: 2, display: 'block' }}>
                  {selectedImportPositions.length} position(s) selected
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} disabled={importing}>
            Cancel
          </Button>
          <Button 
            onClick={handleImportPositions} 
            variant="contained"
            disabled={importing || selectedImportPositions.length === 0}
            startIcon={importing ? <CircularProgress size={20} /> : <Upload />}
          >
            {importing ? 'Importing...' : 'Import Selected'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PositionsSettings;
